/**
 * ForgotPasswordPage - request a password reset email
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch (err) {
      // still show generic sent state
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="forgot-password-page">
      <div className="p-4">
        <button onClick={() => navigate('/auth')} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="forgot-back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <img src="/nlt-radio-logo.png" alt="NLT Radio" className="w-20 h-20 mx-auto mb-6 object-contain" />

          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <CheckCircle2 className="w-14 h-14 text-[#00F0FF] mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>Check your email</h1>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                If an account exists for <span className="text-white">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="mt-8 w-full py-3 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-white font-semibold"
                data-testid="forgot-back-to-login"
              >
                Back to login
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Unbounded' }}>Forgot password?</h1>
              <p className="text-[#A1A1AA] text-sm text-center mb-8">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="w-5 h-5 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full py-3 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-white font-semibold disabled:opacity-50 flex items-center justify-center"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send reset link'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
